import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SongProgress } from '../api/takes';
import type { Song, VoicePart } from '../api/types';
import { dateLocale } from '../i18n';
import { formatDateTime, shortName } from '../lib/format';
import { loopAround } from '../lib/issues';
import { commentAuthor } from '../lib/people';
import type { PracticeState } from '../pages/PracticePage';
import ui from '../styles/ui.module.css';
import { Avatar } from './Avatar';
import { GradeChip } from './Chips';
import styles from './SongsTable.module.css';

const CommentIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M5 5h14a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 16h-8l-4.5 3.5V16H5a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 5 5z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
  </svg>
);

interface Props {
  songs: readonly Song[];
  /** The chorist's part, to say when a song has no such part (the part itself is in the page header). */
  part: VoicePart | null;
  /** The chorist's progress per song id. */
  progress: ReadonlyMap<string, SongProgress>;
}

/**
 * The chorist's songs (wireframe 01): the director's grade, where the latest submission
 * stands, the comment thread (opened in place) and "Çalış". Used on the home page and in the chorists' Kitaplık.
 */
export function SongsTable({ songs, part, progress }: Props) {
  const { t } = useTranslation();
  const [openSong, setOpenSong] = useState<string | null>(null);

  function status(p: SongProgress | undefined): string {
    if (!p) return t('home.status.none');
    if (p.awaitingGrade) return t('home.status.waiting', { when: formatDateTime(p.lastSubmittedAt, dateLocale()) });
    return p.grade ? t(`home.status.${p.grade.source}`) : t('home.status.none');
  }

  return (
    <table className={`${ui.table} ${styles.table}`}>
      <thead>
        <tr>
          <th>{t('home.colSong')}</th>
          <th>{t('home.colGrade')}</th>
          <th>{t('home.colStatus')}</th>
          <th>{t('home.colComments')}</th>
          <th aria-label={t('home.practice')} />
        </tr>
      </thead>
      <tbody>
        {songs.map((song) => {
          const hasPart = !!part && song.summary.parts.some((x) => x.voicePart === part);
          const p = progress.get(song.id);
          const comments = p?.comments ?? [];
          const open = openSong === song.id && comments.length > 0;
          const lastIndex = song.summary.measureCount - 1;
          return (
            <Fragment key={song.id}>
              <tr data-testid="song-row" className={open ? styles.openRow : undefined}>
                <td>
                  {song.title}
                  {song.composer && <span className={ui.secondary}>{song.composer}</span>}
                  {part && !hasPart && (
                    <span className={ui.secondary} data-testid="part-missing">
                      {t('home.partMissing', { part: t(`parts.${part}`) })}
                    </span>
                  )}
                </td>
                <td data-testid="song-grade">
                  <GradeChip grade={p?.grade?.value ?? null} />
                </td>
                <td data-testid="song-status" className={p?.awaitingGrade ? styles.waiting : ui.muted}>
                  {status(p)}
                </td>
                <td>
                  <button
                    type="button"
                    className={open ? `${styles.commentsButton} ${styles.on}` : styles.commentsButton}
                    aria-expanded={open}
                    aria-label={t('home.comments', { count: comments.length })}
                    title={t('home.comments', { count: comments.length })}
                    disabled={comments.length === 0}
                    data-testid="comments-button"
                    onClick={() => setOpenSong(open ? null : song.id)}
                  >
                    <CommentIcon />
                    {comments.length}
                  </button>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <Link to={`/songs/${song.id}`} className={ui.button}>
                    {t('home.practice')}
                  </Link>
                </td>
              </tr>
              {open && (
                <tr className={styles.threadRow}>
                  <td colSpan={5}>
                    <div className={styles.thread} data-testid="comment-thread">
                      <div className={styles.threadHeader}>
                        <strong>{t('home.threadTitle', { song: song.title })}</strong>
                        <span className={ui.muted}>{t('home.newestFirst')}</span>
                      </div>
                      <ul>
                        {comments.map((c) => (
                          <li key={c.id}>
                            <Avatar name={c.authorName ?? t('common.deletedUser')} kind={commentAuthor(c).kind} leadsSection={commentAuthor(c).leadsSection} size="large" />
                            <div>
                              <div className={styles.meta}>
                                <strong>{c.authorName ? shortName(c.authorName) : t('common.deletedUser')}</strong>{' '}
                                <span className={ui.muted}>
                                  {[
                                    commentAuthor(c).roleInText ? t(`roles.${c.authorRole}`) : null,
                                    c.measureNumber ? t('results.bar', { bar: c.measureNumber }) : null,
                                    formatDateTime(c.createdAt, dateLocale()),
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                              </div>
                              <p>{c.body}</p>
                              {c.measureIndex !== null && c.measureNumber && (
                                <Link to={`/songs/${song.id}`} state={{ ...loopAround(c.measureIndex, lastIndex), loop: true } satisfies PracticeState}>
                                  {t('home.practiceBar', { bar: c.measureNumber })}
                                </Link>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
